import { FC, HTMLAttributes, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import clx from "classnames";
import { useDebounce, useOutsideClick, useToggle } from "src/Hooks";
import API from "src/API";
import { CustomerItemType } from "src/Types";
import ClearBtn from "../../Components/ClearBtn/ClearBtn";
import { getGlobalOffset } from "src/Utils";
import { ReactComponent as ArrowIcon } from './arrow.svg';
import st from "./ParticipantSelect.module.scss";
import UserInfo from "../../Components/UserInfo/UserInfo";

interface IParticipantSelectProps extends HTMLAttributes<HTMLDivElement> {

	defaultOpen?: boolean;
	placeholder?: string;
	showClearBtn?: boolean;
	minSearchLength?: number;
	disabled?: boolean;

	value?: string;
	setValue: ( value?: CustomerItemType ) => void;

	onOutsideClick?: ( e: any ) => void;
};

export const ParticipantSelect: FC<IParticipantSelectProps> = ( props ) => {

	const { value, setValue, defaultOpen = false, showClearBtn = false, minSearchLength = 3, placeholder = "", 
		disabled = false, className, onOutsideClick, ...otherProps 
	} = props;

	const wrapperRef = useRef<HTMLDivElement>(null);
	const modalRef = useOutsideClick<HTMLDivElement>( (e) => {
		if ( !!onOutsideClick ) {
			onOutsideClick( e );
		}
		else if ( isOpened ) {
			toggle();
		}
	}, undefined, wrapperRef );
	const [ isOpened, toggle ] = useToggle(defaultOpen);
	const [ isWait, setIsWait ] = useState<boolean>(false);
	const [ data, setData ] = useState<CustomerItemType[]>([]);
	const [ selected, setSelected ] = useState<CustomerItemType>();
	const [ search, setSearch ] = useDebounce<string>( onSearch, "" );

	useEffect( () => {
		if ( !!isOpened && ( minSearchLength === -1 || minSearchLength <= search.length ) ) {
			loadList();
		}
	// eslint-disable-next-line
	}, [ isOpened ] );

	useEffect( () => {
		if ( !!value ) {
			onSetSelectedInfo( data );
		}
	// eslint-disable-next-line
	}, [ value ] );

	function onSearch ( search: any ) {
		if ( !!isOpened && !!search && ( minSearchLength <= 0 || search.length >= minSearchLength ) ) {
			loadList( search );
		}
	};

	const loadList = ( search?: string ) => {
		setIsWait(true);
		API.getParticipants( search, 0, 5 )
			.then( result => {
				setData( result );
				onSetSelectedInfo(result);
			})
			.catch( (e: any) => {
				console.warn(e);
			})
			.finally( () => setIsWait(false) );
	};

	const onSetSelectedInfo = ( list: Array<CustomerItemType> ) => {
		if ( !!value && ( !selected || selected.id !== value ) ) {
			const index = list.findIndex( p => p.id === value );
			if ( index !== -1 ) {
				setSelected( list[index] );
			} 
			else {
				loadSelected();
			}
		}
	};

	const loadSelected = () => {
		if ( !value ) return;
		API.getParticipant( value )
			.then( result => {
				if ( !!result )
					setSelected( result );
				else {
					setValue( undefined );
					setSelected(undefined);
					console.warn( "Не удалось получить от сервера выбранного заявителя! Значение сброшено." );
				}
			})
			.catch( (e: any) => {
				console.warn(e);
			})
			.finally( () => setIsWait(false) );
	};

	const onSearchHandler = ( text: string ) => setSearch(text);

	const onSelected = ( participant: CustomerItemType ) => {
		setValue( participant );
		setSelected( participant );
		toggle();
	};

	const onClearHandler = () => {
		if ( isOpened ) setSearch("");
		else setValue(undefined);
		setData([]);
	};

	const getSearchWarnText = () => {
		const length = minSearchLength - search.length;
		const result = {
			message: '',
			isEmpty: false
		};
		if ( isWait ) 
			result.message = "Ведется поиск...";
		else if ( minSearchLength <= 0 ) 
			result.message =  "";
		// else if ( length > 0 && length < 3 ) 
			// return `Введите ещё ${length} символа`;
		else if ( length > 0 )
			result.message = `Введите минимум ${minSearchLength} символа`;
		else if ( !data.length ) {
			result.message = "По Вашему запросу ничего не найдено";
			result.isEmpty = true;
		}
		return result;
	};

	const getStylesForList = () => {
		const styles = { 
			left: 0,
			top: 0,
			width: 100
		};
		if ( !!wrapperRef.current ) {
			const offset = getGlobalOffset( wrapperRef.current);
			styles.left = offset.left - offset.scroll;
			styles.top = ( offset.top + offset.height ) - offset.scrollTop;
			styles.width = offset.width;
		}
		return styles;
	};

	const renderOption = ( item: CustomerItemType, index: number) => (
		<UserInfo 
			key={ "item_" + index} 
			userName={item.text} 
			userOrg={item.attrs[0]}
			email={item.email}
			className={clx(st.item, { [st.active]: item.id === value } )}
			onClick={() => onSelected(item) }
		/>
	);

	const renderMessage = () => {
		const { message, isEmpty } = getSearchWarnText();
		if ( !message ) return null;
		return (
			<p className={clx( st.message, { [st.error]: isEmpty } )}>
				{message}
			</p>
		);
	};

	const renderBody = () => (
		<div ref={modalRef} className={st.customerListWrapper} style={getStylesForList()}>
			{ renderMessage() }
			<ul className={st.wrapper}>
				{ data.map( renderOption )}
			</ul>
		</div>
	);

	const renderTb = () => (
		<div className={st.toolbar}>
			{ !!isWait && <div className={st.spinner} /> }
			{ 
				( showClearBtn || ( isOpened && !!search ) ) && 
				<ClearBtn
					disable={ disabled || ( isOpened ? !search : !value ) }
					onClick={onClearHandler}
				/> 
			}
		</div>
	);

	const classes = clx( st.customerSelect, className, {
		[st.opened]: isOpened
	});

	return (
		<div ref={wrapperRef} {...otherProps} className={classes}>
			<ArrowIcon onClick={toggle}/>
			<input
				value={ (isOpened ? search : (selected?.text || "")) }
				placeholder={ isOpened ? "Введите ФИО для поиска" : placeholder}
				readOnly={ disabled || !isOpened }
				onClick={ () => !isOpened && toggle() }
				onChange={ e => onSearchHandler(e.target.value) }
			/>
			{ renderTb() }
			{ isOpened && createPortal( renderBody(), document.body) }
		</div>
	);
};

export default ParticipantSelect;
